/**
 * AI Implementation Simulator — Main Entry Point
 * Flow: Landing Page → Avatar Creator → Chat with Owl Team
 */

import { imageGenerator } from './image';
import { uiManager } from './ui/UIManager';
import { LandingPage } from './ui/LandingPage';
import { PLAYER_APPEARANCE_KEY, AvatarCreator } from './ui/AvatarCreator';
import { ChatScreen } from './ui/ChatScreen';
import { createSession, getSession, saveAvatarToServer, loadAvatarFromServer, deleteSession } from './api/chat';
import { getPublicProfile, getProfileCity, type ClientProfile } from './api/profiles';
// owl-avatar module no longer needed — using static brand SVG
import type { OwlId } from './types/owl';

// Expose imageGenerator globally
(window as any).imageGenerator = imageGenerator;

// Session persistence
const SESSION_KEY = 'ais_session_id';
const AVATAR_KEY = 'ais_avatar_url';
const AVATAR_PERSISTENT_KEY = 'ais_avatar_persistent'; // NEVER cleared on reset — survives conversation resets
const NAME_KEY = 'ais_player_name';
const NAME_PERSISTENT_KEY = 'ais_player_name_persistent'; // NEVER cleared on reset
const PATH_KEY = 'ais_path';

// In-memory avatar for current session (full resolution)
let currentAvatarUrl: string = '';

// All owls use the same brand owl icon
const OWL_ICON = '/brand/owl-icon.png';
let owlAvatars: Record<OwlId, string> = {
    poly: OWL_ICON,
    nova: OWL_ICON,
    atlas: OWL_ICON,
    ember: OWL_ICON,
    ledger: OWL_ICON,
    scout: OWL_ICON,
};

let selectedPath: 'discovery' | 'strategy' = 'discovery';
let activeClientSlug: string | null = null;
let activeClientProfile: ClientProfile | null = null;
const GLOBAL_IDENTITY_KEYS = new Set([
    AVATAR_KEY,
    AVATAR_PERSISTENT_KEY,
    NAME_KEY,
    NAME_PERSISTENT_KEY,
]);

function scopedKey(key: string): string {
    if (GLOBAL_IDENTITY_KEYS.has(key)) return key;
    return activeClientSlug ? `${key}:${activeClientSlug}` : key;
}

function legacyScopedKey(key: string): string | null {
    if (!activeClientSlug) return null;
    return `${key}:${activeClientSlug}`;
}

function storeGet(key: string): string | null {
    const value = localStorage.getItem(scopedKey(key));
    if (value || !GLOBAL_IDENTITY_KEYS.has(key)) return value;

    const legacyKey = legacyScopedKey(key);
    return legacyKey ? localStorage.getItem(legacyKey) : null;
}

function storeSet(key: string, value: string) {
    localStorage.setItem(scopedKey(key), value);
}

function storeRemove(key: string) {
    localStorage.removeItem(scopedKey(key));
}

function migrateLegacyScopedIdentity() {
    if (!activeClientSlug) return;

    GLOBAL_IDENTITY_KEYS.forEach((key) => {
        const legacyKey = legacyScopedKey(key);
        if (!legacyKey || localStorage.getItem(key)) return;

        const legacyValue = localStorage.getItem(legacyKey);
        if (legacyValue) {
            localStorage.setItem(key, legacyValue);
        }
    });
}

function getClientSlugFromPath(): string | null {
    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
    if (!firstSegment || firstSegment === 'admin') return null;
    if (!/^[a-z0-9][a-z0-9-]{1,80}$/.test(firstSegment)) return null;
    return firstSegment;
}

/**
 * Compress a base64 data URL down to a small thumbnail for localStorage
 */
function compressAvatar(dataUrl: string, maxDim: number = 400): Promise<string> {
    return new Promise((resolve) => {
        if (!dataUrl || !dataUrl.startsWith('data:')) {
            resolve(dataUrl);
            return;
        }
        const img = new Image();
        img.onload = () => {
            // Preserve aspect ratio
            let w = img.naturalWidth;
            let h = img.naturalHeight;
            if (w > maxDim || h > maxDim) {
                const scale = maxDim / Math.max(w, h);
                w = Math.round(w * scale);
                h = Math.round(h * scale);
            }
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = () => resolve('skipped');
        img.src = dataUrl;
    });
}

/**
 * Save avatar — full res in memory, compressed thumbnail in localStorage
 * Avatar persists until the user explicitly deletes or changes it.
 */
async function saveAvatar(avatarUrl: string) {
    currentAvatarUrl = avatarUrl;
    if (!avatarUrl || avatarUrl === 'skipped') {
        try { storeSet(AVATAR_KEY, 'skipped'); } catch {}
        // Don't overwrite persistent avatar with 'skipped' — keep whatever was there
        return;
    }
    try {
        const thumbnail = await compressAvatar(avatarUrl);
        storeSet(AVATAR_KEY, thumbnail);
        // Also save to persistent key that survives conversation resets
        storeSet(AVATAR_PERSISTENT_KEY, thumbnail);
        console.log('[App] Avatar saved to localStorage (session + persistent), thumbnail size:', Math.round(thumbnail.length / 1024), 'KB');
    } catch (e) {
        console.warn('[App] Could not save avatar thumbnail to localStorage:', e);
        try {
            const smallThumb = await compressAvatar(avatarUrl, 128);
            storeSet(AVATAR_KEY, smallThumb);
            storeSet(AVATAR_PERSISTENT_KEY, smallThumb);
            console.log('[App] Avatar saved with smaller compression');
        } catch {
            const existing = storeGet(AVATAR_KEY);
            if (!existing || existing === 'skipped') {
                try { storeSet(AVATAR_KEY, 'skipped'); } catch {}
            }
        }
    }
}

import { AdminPortal } from './ui/AdminPortal';

async function init() {
    console.log('[AIS] Initializing AI Implementation Simulator...');

    // Secret Admin Dashboard route bypass (Localhost ONLY)
    const checkAdminRoute = async () => {
        if (window.location.hash === '#letsgo' || window.location.pathname === '/admin') {
            const portal = new AdminPortal();
            await portal.mount();
            return true;
        }
        return false;
    };

    if (await checkAdminRoute()) return;

    activeClientSlug = getClientSlugFromPath();
    if (activeClientSlug) {
        activeClientProfile = await getPublicProfile(activeClientSlug);
        if (!activeClientProfile) {
            showClientProfileNotFound(activeClientSlug);
            return;
        }
        migrateLegacyScopedIdentity();
        console.log('[App] Loaded client profile:', activeClientProfile.slug);
    }

    window.addEventListener('hashchange', async () => {
        if (await checkAdminRoute()) {
            console.log('[App] Intercepted admin route dynamically');
        }
    });

    console.log('[App] Using backend-proxied AI providers and brand owl icon for all agents');

    // Check for existing session
    const savedSessionId = storeGet(SESSION_KEY);
    const savedAvatarUrl = storeGet(AVATAR_KEY);
    const savedName = storeGet(NAME_KEY);

    if (savedSessionId && savedName) {
        try {
            const session = await getSession(savedSessionId);
            if (session && session.is_active) {
                console.log('[App] Found existing session:', savedSessionId);

                // Try localStorage first, then persistent key, then server for avatar
                let restoredAvatar = savedAvatarUrl || '';
                if (!restoredAvatar || restoredAvatar === 'skipped') {
                    // Check the persistent avatar key (survives resets)
                    const persistentAvatar = storeGet(AVATAR_PERSISTENT_KEY);
                    if (persistentAvatar && persistentAvatar !== 'skipped' && persistentAvatar.startsWith('data:')) {
                        restoredAvatar = persistentAvatar;
                        try { storeSet(AVATAR_KEY, restoredAvatar); } catch {}
                        console.log('[App] Avatar restored from persistent key');
                    } else {
                        console.log('[App] No avatar in localStorage, checking server...');
                        const serverAvatar = await loadAvatarFromServer(savedSessionId);
                        if (serverAvatar && serverAvatar.startsWith('data:')) {
                            restoredAvatar = serverAvatar;
                            try { storeSet(AVATAR_KEY, restoredAvatar); } catch {}
                            try { storeSet(AVATAR_PERSISTENT_KEY, restoredAvatar); } catch {}
                            console.log('[App] Avatar restored from server');
                        }
                    }
                }

                currentAvatarUrl = restoredAvatar;
                console.log('[App] Restored avatar:', restoredAvatar && restoredAvatar !== 'skipped' ? `${Math.round(restoredAvatar.length / 1024)}KB` : 'none');
                showResumePrompt(session, savedName, restoredAvatar);
                return;
            }
        } catch (err) {
            console.warn('[App] Failed to resume session, starting fresh');
        }
    }

    // No active session — check if we have a persistent avatar from a previous session
    // This happens after a conversation reset: session is gone but avatar should survive
    const persistentAvatar = storeGet(AVATAR_PERSISTENT_KEY);
    const persistentName = storeGet(NAME_PERSISTENT_KEY);
    if (persistentAvatar && persistentAvatar !== 'skipped' && persistentAvatar.startsWith('data:')) {
        currentAvatarUrl = persistentAvatar;
        // Re-populate the session avatar key so the avatar creator can skip photo
        try { storeSet(AVATAR_KEY, persistentAvatar); } catch {}
        if (persistentName) {
            try { storeSet(NAME_KEY, persistentName); } catch {}
        }
        console.log('[App] Persistent avatar found after reset, pre-populating for new session');
    }

    showLanding();
}

/**
 * Show a resume prompt with the saved avatar and option to recreate
 */
function showResumePrompt(session: any, name: string, avatarUrl: string) {
    const container = document.createElement('div');
    container.className = 'landing-page';

    const hasAvatar = avatarUrl && avatarUrl !== 'skipped' && avatarUrl.startsWith('data:');

    container.innerHTML = `
        <div style="margin-bottom: 1.5rem;">
            ${hasAvatar
                ? `<img src="${avatarUrl}" class="landing-avatar" alt="${name}" />`
                : `<div class="landing-avatar" style="display:flex;align-items:center;justify-content:center;width:140px;height:140px;margin:0 auto 2rem;background:var(--color-accent);color:white;font-size:3rem;font-family:var(--font-display);">${name.charAt(0).toUpperCase()}</div>`
            }
        </div>

        <h1 class="landing-title" style="font-size: 2rem;">Welcome back, ${name}!</h1>
        <p class="landing-subtitle">Pick up where you left off, or start a new conversation${hasAvatar ? ' with the same avatar' : ''}.</p>

        <div style="display: flex; flex-direction: column; gap: 0.75rem; align-items: center;">
            <button id="resume-btn" class="btn" style="width: 260px;">Continue Session</button>
            <button id="new-conversation-btn" class="btn btn-outline" style="width: 260px;">Start New Conversation</button>
            <button id="start-over-btn" style="background:none;border:none;color:var(--color-text-muted);cursor:pointer;font-size:0.85rem;font-family:var(--font-main);text-decoration:underline;padding:0.5rem;">Start completely over</button>
        </div>
    `;

    uiManager.renderScreen(container);

    container.querySelector('#resume-btn')!.addEventListener('click', () => {
        launchChat(
            session.id, name, avatarUrl,
            session.chat_history, undefined,
            !hasAvatar,
            session.current_agent as OwlId,
            session.current_gear, session.current_phase, session.path
        );
    });

    container.querySelector('#new-conversation-btn')!.addEventListener('click', async (e) => {
        const btn = e.currentTarget as HTMLButtonElement;
        btn.disabled = true;
        btn.textContent = 'Starting...';
        try {
            await startNewConversation(session, name, avatarUrl, !!hasAvatar);
        } catch (err: any) {
            console.error('[App] Failed to start a new conversation:', err);
            alert(`Could not start a new conversation: ${err?.message || err}. Please try again.`);
            btn.disabled = false;
            btn.textContent = 'Start New Conversation';
        }
    });

    container.querySelector('#start-over-btn')!.addEventListener('click', async () => {
        // Soft-delete the session on the server (recoverable, not permanently lost)
        const sid = storeGet(SESSION_KEY);
        if (sid) {
            deleteSession(sid).catch(() => {});
        }
        // "Start completely over" clears EVERYTHING including avatar
        storeRemove(SESSION_KEY);
        storeRemove(AVATAR_KEY);
        storeRemove(AVATAR_PERSISTENT_KEY);
        try { localStorage.removeItem(PLAYER_APPEARANCE_KEY); } catch { /* storage unavailable */ }
        storeRemove(NAME_KEY);
        storeRemove(NAME_PERSISTENT_KEY);
        storeRemove(PATH_KEY);
        currentAvatarUrl = '';
        showLanding();
    });
}

/**
 * Close the current conversation and open a fresh one, keeping the same name and avatar
 */
async function startNewConversation(oldSession: any, name: string, avatarUrl: string, hasAvatar: boolean) {
    selectedPath = oldSession.path || storeGet(PATH_KEY) || 'discovery';

    // Soft-delete the old conversation on the server (recoverable)
    deleteSession(oldSession.id).catch(() => {});
    storeRemove(SESSION_KEY);

    const session = await createSession(name, '', selectedPath, activeClientProfile?.slug);
    storeSet(SESSION_KEY, session.id);
    storeSet(NAME_KEY, name);
    storeSet(NAME_PERSISTENT_KEY, name);
    storeSet(PATH_KEY, selectedPath);

    if (hasAvatar) {
        saveAvatarToServer(session.id, avatarUrl).catch(() => {});
    }

    launchChat(
        session.id, name, hasAvatar ? avatarUrl : 'skipped', [],
        session.opening_message, !hasAvatar,
        'poly', 1, '1.1', selectedPath
    );
}

function showLanding() {
    const polyAvatar = owlAvatars.poly;
    const landing = new LandingPage((path) => {
        selectedPath = path;
        showAvatarCreator();
    }, polyAvatar, activeClientProfile || undefined);
    landing.mount();
}

function showClientProfileNotFound(slug: string) {
    const container = document.createElement('div');
    container.className = 'landing-page';
    container.innerHTML = `
        <h1 class="landing-title" style="font-size: 2rem;">Client profile not found</h1>
        <p class="landing-subtitle">No active profile exists for <code>${slug}</code>.</p>
        <button id="home-btn" class="btn" style="width: 240px;">Go Home</button>
    `;
    uiManager.renderScreen(container);
    container.querySelector('#home-btn')?.addEventListener('click', () => {
        window.location.href = '/';
    });
}

function showAvatarCreator(existingSessionId?: string) {
    // Pre-populate name from persistent storage if available
    const savedName = storeGet(NAME_PERSISTENT_KEY) || storeGet(NAME_KEY) || '';
    const creator = new AvatarCreator(async (name, avatarUrl, skippedPhoto) => {
        console.log('[App] Avatar created:', name, skippedPhoto ? '(skipped photo)' : `(url length: ${avatarUrl.length})`);

        // If user skipped photo but we have a persistent avatar, USE IT
        if (skippedPhoto || !avatarUrl || avatarUrl === 'skipped') {
            const persistentAvatar = storeGet(AVATAR_PERSISTENT_KEY);
            if (persistentAvatar && persistentAvatar !== 'skipped' && persistentAvatar.startsWith('data:')) {
                avatarUrl = persistentAvatar;
                skippedPhoto = false; // We DO have an avatar
                console.log('[App] Using persistent avatar instead of skipping');
            }
        }

        try {
            let sessionId = existingSessionId;

            // Save avatar (compressed thumbnail to localStorage, full in memory)
            await saveAvatar(avatarUrl || 'skipped');

            if (!sessionId) {
                // Create new session
                const session = await createSession(name, '', selectedPath, activeClientProfile?.slug);
                sessionId = session.id;

                storeSet(SESSION_KEY, session.id);
                storeSet(NAME_KEY, name);
                storeSet(NAME_PERSISTENT_KEY, name);
                storeSet(PATH_KEY, selectedPath);

                // Save avatar to server (fire-and-forget, don't block)
                if (avatarUrl && avatarUrl !== 'skipped') {
                    saveAvatarToServer(session.id, avatarUrl).catch(() => {});
                }

                launchChat(
                    session.id, name, currentAvatarUrl, [],
                    session.opening_message, skippedPhoto,
                    'poly', 1, '1.1', selectedPath
                );
            } else {
                // Reuse existing session, just update avatar
                storeSet(NAME_KEY, name);
                storeSet(NAME_PERSISTENT_KEY, name);

                // Save avatar to server
                if (avatarUrl && avatarUrl !== 'skipped') {
                    saveAvatarToServer(sessionId, avatarUrl).catch(() => {});
                }

                const session = await getSession(sessionId);
                if (session) {
                    launchChat(
                        session.id, name, currentAvatarUrl,
                        session.chat_history, undefined,
                        skippedPhoto,
                        session.current_agent as OwlId,
                        session.current_gear, session.current_phase, session.path
                    );
                }
            }
        } catch (err) {
            console.error('[App] Failed to create/resume session:', err);
            throw err;
        }
    }, savedName, storeGet(AVATAR_PERSISTENT_KEY) || '');
    creator.mount();
}

function launchChat(
    sessionId: string,
    playerName: string,
    playerAvatarUrl: string,
    existingHistory?: any[],
    openingMessage?: string,
    skippedPhoto?: boolean,
    currentAgent?: OwlId,
    currentGear?: number,
    currentPhase?: string,
    path?: string
) {
    const chatScreen = new ChatScreen({
        sessionId,
        playerName,
        playerAvatarUrl: playerAvatarUrl || '',
        owlAvatars,
        openingMessage,
        existingHistory: existingHistory && existingHistory.length > 0 ? existingHistory : undefined,
        currentAgent: currentAgent || 'poly',
        currentGear: currentGear || 1,
        currentPhase: currentPhase || '1.1',
        path: path || 'discovery',
        capOnly: skippedPhoto || playerAvatarUrl === 'skipped' || !playerAvatarUrl,
        clientCity: activeClientProfile ? getProfileCity(activeClientProfile) : undefined,
        clientContext: activeClientProfile ? `${activeClientProfile.display_name}\n${activeClientProfile.markdown}` : undefined
    });
    chatScreen.mount();
}

// Boot
init();
